import { utilityServesState, type UtilityListItem } from '../types/utility';

/**
 * The picker's ordering rules, kept out of the component so they can be tested
 * without a DOM.
 *
 * The national table holds ~3,400 utilities, so the picker never lists them
 * all. It offers the ones that serve the customer's ZIP — a median of 2, and
 * 16 at the very worst — and typing searches the whole table from the server.
 * utilities.md's rule that a user must never fail to see their utility is met
 * by the search reaching every row, not by rendering every row.
 */

/**
 * The seeded catch-all row ('Other (not listed)') is never offered as a
 * utility: the picker's own "not listed" entry is the single affordance for
 * that, and two of them reading differently is how a project ends up with a
 * utility_id pointing at a placeholder.
 */
export function isSelectableUtility(u: Pick<UtilityListItem, 'name'>): boolean {
  return !u.name.trim().toLowerCase().startsWith('other');
}

/** Collapse whitespace and case so "  Green   Mountain " matches "green mountain". */
export function normalizeQuery(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * PostgREST reads some punctuation as filter syntax. A utility name never
 * needs it, so it is dropped rather than escaped — the remaining words still
 * match through ilike.
 */
export function sanitizeSearchTerm(raw: string): string {
  return normalizeQuery(raw).replace(/[%_,()*\\]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Two letters, uppercase, or null. */
export function stateCodeOf(raw: string | undefined | null): string | null {
  const value = (raw ?? '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(value) ? value : null;
}

function customers(u: UtilityListItem): number {
  return typeof u.residential_customers === 'number' ? u.residential_customers : -1;
}

/**
 * 0 when TrueLoad knows a way to this utility's data, 1 when it does not.
 *
 * Customer count alone gets deregulated markets backwards. In Texas the wires
 * company holds the meter but the retail providers hold the customers, so
 * EIA-861 reports Oncor with zero residential customers and ranking by size
 * buried it under small co-ops for every Dallas ZIP — the one utility a
 * contractor there actually needs, last on the list. A known route is the
 * better signal for "this is probably the answer", and it is the honest one:
 * it is the utility TrueLoad can get readings from.
 */
function routeRank(u: UtilityListItem): number {
  return u.ami_status && u.ami_status !== 'none' ? 0 : 1;
}

/**
 * Order search results so the likely answer is first: the customer's own state
 * before anywhere else, a name that starts with what was typed before one that
 * merely contains it, then the utility with the most residential customers.
 * Size last is what puts Georgia Power above 'Georgia Power Employees Credit
 * Union'-shaped rows without hard-coding either.
 */
export function rankUtilities(
  rows: UtilityListItem[],
  query: string,
  state: string | null
): UtilityListItem[] {
  const q = normalizeQuery(query);
  const seen = new Set<string>();
  const unique = rows.filter((u) => {
    if (seen.has(u.id) || !isSelectableUtility(u)) return false;
    seen.add(u.id);
    return true;
  });
  return unique.sort((a, b) => {
    const aState = state && utilityServesState(a, state) ? 0 : 1;
    const bState = state && utilityServesState(b, state) ? 0 : 1;
    if (aState !== bState) return aState - bState;
    if (q) {
      const aStarts = a.name.toLowerCase().startsWith(q) ? 0 : 1;
      const bStarts = b.name.toLowerCase().startsWith(q) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;
    }
    const byRoute = routeRank(a) - routeRank(b);
    if (byRoute !== 0) return byRoute;
    const bySize = customers(b) - customers(a);
    if (bySize !== 0) return bySize;
    return a.name.localeCompare(b.name);
  });
}

/**
 * The label in the list. The state is shown only when it is not the project's
 * own, so an in-state list stays clean and a national search stays unambiguous
 * — there are several 'City of Fairview'-style names across states.
 */
export function utilityOptionLabel(u: UtilityListItem, projectState: string | null): string {
  if (!u.state) return u.name;
  if (projectState && u.state.toUpperCase() === projectState) return u.name;
  return `${u.name} (${u.state})`;
}

/** Client-side narrowing of an already-fetched list, for the no-network path. */
export function filterUtilities(rows: UtilityListItem[], query: string): UtilityListItem[] {
  const q = normalizeQuery(query);
  if (!q) return rows.filter(isSelectableUtility);
  return rows.filter((u) => isSelectableUtility(u) && u.name.toLowerCase().includes(q));
}
